import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTransfers, createTransfer, approveTransfer, rejectTransfer,
  fetchAdminWallets, clearWalletState, createDailySupply,
} from "../../redux/Slices/WalletSlice";
import { toast } from "react-toastify";
import { FaPlus, FaFilter, FaExchangeAlt, FaCheck, FaTimes, FaInfoCircle } from "react-icons/fa";
import { useTitle } from "../../context/TitleContext";
import { showConfirm } from "../../Utils/SwalConfirm";
import Swal from "sweetalert2";
import { getUserProfile } from "../../redux/Slices/authSlice";

/* ── helpers ─────────────────────────────────────────────── */
const formatCurrency = (val) =>
  val !== undefined && val !== null ? `${Number(val).toLocaleString("ar-EG", { minimumFractionDigits: 2 })} ج.م` : "—";

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleDateString("ar-EG", { year: "numeric", month: "short", day: "numeric" });
};

const getAdminName = (obj) => {
  if (!obj) return "—";
  return obj.name_ar || obj.name || obj.user_name || `موظف #${obj.id || obj.admin_id}`;
};

const isPending = (s) => !s || s === "pending" || s === 0;
const isApproved = (s) => s === "approved" || s === "accepted" || s === 1;
const isRejected = (s) => s === "rejected" || s === 2;

/* ── Status pill ─────────────────────────────────────────── */
const StatusPill = ({ status }) => {
  if (isApproved(status)) return <span style={{ padding: "4px 12px", borderRadius: 20, background: "#d1fae5", color: "#065f46", fontSize: 12, fontWeight: 700 }}>✓ مقبول</span>;
  if (isRejected(status)) return <span style={{ padding: "4px 12px", borderRadius: 20, background: "#fee2e2", color: "#991b1b", fontSize: 12, fontWeight: 700 }}>✕ مرفوض</span>;
  return <span style={{ padding: "4px 12px", borderRadius: 20, background: "#fef3c7", color: "#92400e", fontSize: 12, fontWeight: 700 }}>⏳ قيد الانتظار</span>;
};

/* ── Type pill ───────────────────────────────────────────── */
const TypePill = ({ type }) =>
  type === "transfer"
    ? <span style={{ padding: "4px 12px", borderRadius: 20, background: "#dbeafe", color: "#1e40af", fontSize: 12, fontWeight: 700 }}>⇄ تحويل</span>
    : <span style={{ padding: "4px 12px", borderRadius: 20, background: "#ede9fe", color: "#5b21b6", fontSize: 12, fontWeight: 700 }}>⬆ توريد</span>;

/* ── Pagination ──────────────────────────────────────────── */
const PaginationBar = ({ meta, currentPage, loading, onChange }) => {
  if (!meta || meta.last_page <= 1) return null;
  const s = Math.max(1, currentPage - 2), e = Math.min(meta.last_page, currentPage + 2);
  const pages = [];
  for (let i = s; i <= e; i++) {
    pages.push(
      <button key={i} onClick={() => i !== currentPage && onChange(i)} disabled={loading}
        style={{ width: 36, height: 36, borderRadius: 8, border: "none", fontWeight: i === currentPage ? 700 : 400,
          background: i === currentPage ? "var(--red-color)" : "#f1f5f9", color: i === currentPage ? "#fff" : "#475569", cursor: "pointer" }}>
        {i}
      </button>
    );
  }
  const nb = (dis, lbl, fn) => (
    <button onClick={fn} disabled={dis} style={{ padding: "0 14px", height: 36, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: dis ? "#cbd5e1" : "#475569", cursor: dis ? "not-allowed" : "pointer", fontWeight: 500 }}>{lbl}</button>
  );
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
      {nb(currentPage === 1 || loading, "السابق", () => onChange(currentPage - 1))}
      <div style={{ display: "flex", gap: 4 }}>{pages}</div>
      {nb(currentPage === meta.last_page || loading, "التالي", () => onChange(currentPage + 1))}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Main
══════════════════════════════════════════════════════════ */
const Transfers = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { transfers, transfersMeta, adminWallets, loading, actionLoading, error, success } = useSelector((s) => s.wallets);
  const { user, profile } = useSelector((s) => s.auth);

  // BUG-75 fix: تحديد هوية الموظف المسجل دخوله حالياً حتى نستبعده من قائمة "الموظف المستلم" (منع التحويل لنفس الحساب)
  const getLoggedUserId = () => {
    if (profile?.id) return profile.id;
    if (user?.data?.id) return user.data.id;
    if (user?.id) return user.id;
    const local = localStorage.getItem("loggedUser");
    if (local) {
      try {
        const parsed = JSON.parse(local);
        const data = parsed?.data || parsed;
        if (data?.id) return data.id;
      } catch {}
    }
    return null;
  };
  const loggedUserId = getLoggedUserId();

  const [selectedType, setSelectedType]     = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dateFrom, setDateFrom]             = useState("");
  const [dateTo, setDateTo]                 = useState("");
  const [currentPage, setCurrentPage]       = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toAdminId, setToAdminId]           = useState("");
  const [amount, setAmount]                 = useState("");
  const [transferDate, setTransferDate]     = useState(new Date().toISOString().split("T")[0]);

  const [fromFocused, setFromFocused]       = useState(false);
  const [toFocused, setToFocused]           = useState(false);

  useEffect(() => {
    setTitle("تحويلات وتوريدات الخزائن");
    dispatch(fetchAdminWallets({ page: 1, limit: 100 }));
    // BUG-75 fix: تحميل بيانات البروفايل للحصول على هوية الموظف الحالي
    dispatch(getUserProfile());
  }, [dispatch, setTitle]);

  const loadTransfers = useCallback((page = 1) => {
    dispatch(fetchTransfers({ type: selectedType, status: selectedStatus, from: dateFrom, to: dateTo, page }));
  }, [dispatch, selectedType, selectedStatus, dateFrom, dateTo]);

  useEffect(() => { loadTransfers(currentPage); }, [currentPage, loadTransfers]);

  useEffect(() => {
    if (success) { toast.success(success); dispatch(clearWalletState()); loadTransfers(1); setCurrentPage(1); }
    if (error)   { toast.error(error);   dispatch(clearWalletState()); }
  }, [success, error, dispatch, loadTransfers]);

  const handleFilterSubmit = (e) => { e.preventDefault(); setCurrentPage(1); loadTransfers(1); };
  const handleClearFilters = () => { setSelectedType(""); setSelectedStatus(""); setDateFrom(""); setDateTo(""); setCurrentPage(1); };

  const handleCreateTransferSubmit = (e) => {
    e.preventDefault();
    if (!toAdminId) { toast.error("يرجى اختيار الموظف المستلم"); return; }
    if (!amount || isNaN(amount) || Number(amount) <= 0) { toast.error("يرجى إدخال مبلغ صحيح أكبر من الصفر"); return; }
    if (!transferDate) { toast.error("يرجى اختيار تاريخ العملية"); return; }
    dispatch(createTransfer({ to_admin_id: toAdminId, amount, date: transferDate })).then((res) => {
      if (!res.error) { setShowCreateModal(false); setToAdminId(""); setAmount(""); setTransferDate(new Date().toISOString().split("T")[0]); }
    });
  };

  const handleApprove = async (id) => {
    const ok = await showConfirm({ title: "تأكيد قبول العملية", text: "هل أنت متأكد من قبول هذا التحويل؟", icon: "question", confirmButtonColor: "#28a745", confirmButtonText: "قبول الآن" });
    if (ok) dispatch(approveTransfer({ transferId: id })).then((r) => { if (!r.error) loadTransfers(currentPage); });
  };

  const handleReject = async (id) => {
    const ok = await showConfirm({ title: "تأكيد رفض العملية", text: "هل أنت متأكد من رفض هذا التحويل؟", icon: "warning", confirmButtonColor: "#dc3545", confirmButtonText: "رفض الآن" });
    if (ok) dispatch(rejectTransfer({ transferId: id })).then((r) => { if (!r.error) loadTransfers(currentPage); });
  };

  const handleCreateSupply = async () => {
    const today = new Date().toISOString().split("T")[0];
    const { value: result } = await Swal.fire({
      title: "تسوية التوريد اليومي",
      html: `<div style="text-align:right;direction:rtl;font-family:inherit"><p style="font-size:1rem;margin-bottom:12px;color:#495057;font-weight:bold">1. تاريخ التوريد:</p><div class="form-check mb-2" style="padding-right:1.5em;padding-left:0"><input class="form-check-input" type="radio" name="supplyDateOption" id="dateToday" value="today" checked style="float:right;margin-right:-1.5em;margin-left:0"><label class="form-check-label" for="dateToday" style="margin-right:10px">تصفية عهدة اليوم الحالي (${today})</label></div><div class="form-check mb-2" style="padding-right:1.5em;padding-left:0"><input class="form-check-input" type="radio" name="supplyDateOption" id="dateCustom" value="custom" style="float:right;margin-right:-1.5em;margin-left:0"><label class="form-check-label" for="dateCustom" style="margin-right:10px">اختيار تاريخ مخصص</label></div><div id="customDateWrapper" style="display:none" class="mt-2 mb-3"><label for="customSupplyDate" class="form-label" style="font-size:0.85rem;color:#6c757d">حدد التاريخ المخصص:</label><input type="date" id="customSupplyDate" class="form-control" value="${today}" style="border-radius:8px;padding:6px 12px"></div><hr style="margin:15px 0"><p style="font-size:1rem;margin-bottom:12px;color:#495057;font-weight:bold">2. قيمة التوريد:</p><div class="form-check mb-2" style="padding-right:1.5em;padding-left:0"><input class="form-check-input" type="radio" name="supplyAmountOption" id="amountFull" value="full" checked style="float:right;margin-right:-1.5em;margin-left:0"><label class="form-check-label" for="amountFull" style="margin-right:10px">كامل الرصيد المتوفر في الخزنة</label></div><div class="form-check mb-2" style="padding-right:1.5em;padding-left:0"><input class="form-check-input" type="radio" name="supplyAmountOption" id="amountCustom" value="custom" style="float:right;margin-right:-1.5em;margin-left:0"><label class="form-check-label" for="amountCustom" style="margin-right:10px">تحديد مبلغ يدوي مخصص</label></div><div id="customAmountWrapper" style="display:none" class="mt-2"><label for="customSupplyAmount" class="form-label" style="font-size:0.85rem;color:#6c757d">المبلغ المالي (ج.م):</label><input type="number" id="customSupplyAmount" class="form-control" placeholder="أدخل قيمة المبلغ..." style="border-radius:8px;padding:6px 12px" min="0.01" step="0.01"></div></div>`,
      focusConfirm: false, showCancelButton: true, confirmButtonText: "نعم، توريد الآن", cancelButtonText: "إلغاء",
      confirmButtonColor: "#28a745", cancelButtonColor: "#6c757d",
      didOpen: () => {
        const p = Swal.getPopup();
        p.querySelector('#dateCustom').addEventListener('change', () => (p.querySelector('#customDateWrapper').style.display = 'block'));
        p.querySelector('#dateToday').addEventListener('change', () => (p.querySelector('#customDateWrapper').style.display = 'none'));
        p.querySelector('#amountCustom').addEventListener('change', () => (p.querySelector('#customAmountWrapper').style.display = 'block'));
        p.querySelector('#amountFull').addEventListener('change', () => (p.querySelector('#customAmountWrapper').style.display = 'none'));
      },
      preConfirm: () => {
        const p = Swal.getPopup();
        const isCustomDate = p.querySelector('#dateCustom').checked;
        const customDate   = p.querySelector('#customSupplyDate').value;
        const isCustomAmt  = p.querySelector('#amountCustom').checked;
        const customAmt    = p.querySelector('#customSupplyAmount').value;
        if (isCustomDate && !customDate) { Swal.showValidationMessage('يرجى تحديد التاريخ المخصص'); return false; }
        if (isCustomAmt && (!customAmt || isNaN(customAmt) || Number(customAmt) <= 0)) { Swal.showValidationMessage('يرجى إدخال مبلغ توريد صحيح'); return false; }
        return { date: isCustomDate ? customDate : today, amount: isCustomAmt ? Number(customAmt) : null };
      },
    });
    if (result) dispatch(createDailySupply({ date: result.date, amount: result.amount }));
  };

  /* ── input style ─ */
  const inputS = { width: "100%", padding: "9px 14px", borderRadius: 9, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fff" };
  const labelS = { fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 };
  const focus  = (e) => (e.target.style.borderColor = "var(--red-color)");
  const blur   = (e) => (e.target.style.borderColor = "#e2e8f0");

  /* overlay */
  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <FaExchangeAlt style={{ color: "var(--red-color)" }} /> تحويلات وتوريدات الخزائن
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>إدارة عمليات التحويل والتوريد بين الخزائن</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleCreateSupply} disabled={actionLoading}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,var(--green-color),#059669)", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            <FaPlus size={12} /> توريد عهدة اليوم
          </button>
          <button onClick={() => setShowCreateModal(true)} disabled={actionLoading}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            <FaPlus size={12} /> تحويل جديد
          </button>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────── */}
      <style>{`
        .custom-select {
          width: 100% !important;
          padding: 9px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
        }
        .custom-select:focus {
          border-color: var(--red-color) !important;
        }
        .custom-date {
          width: 100% !important;
          padding: 9px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          color: #0f172a !important;
        }
        .custom-date:focus {
          border-color: var(--red-color) !important;
        }
        .custom-date::placeholder {
          color: #94a3b8 !important;
        }
        .custom-date.empty {
          color: #94a3b8 !important;
        }
      `}</style>
      <form onSubmit={handleFilterSubmit}
        style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, alignItems: "flex-end" }}>
          <div>
            <label style={labelS}>نوع العملية</label>
            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="custom-select">
              <option value="">كل العمليات</option>
              <option value="transfer">⇄ تحويل (أدمن → أدمن)</option>
              <option value="supply">⬆ توريد (أدمن → سوبر أدمن)</option>
            </select>
          </div>
          <div>
            <label style={labelS}>حالة العملية</label>
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="custom-select">
              <option value="">كل الحالات</option>
              <option value="pending">⏳ قيد الانتظار</option>
              <option value="approved">✓ مقبول</option>
              <option value="rejected">✕ مرفوض</option>
            </select>
          </div>
          <div>
            <label style={labelS}>من تاريخ</label>
            <input
              type={fromFocused || dateFrom ? "date" : "text"}
              placeholder="اختر تاريخ البدء"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              onFocus={() => setFromFocused(true)}
              onBlur={() => setFromFocused(false)}
              className={`custom-date${!dateFrom ? " empty" : ""}`}
            />
          </div>
          <div>
            <label style={labelS}>إلى تاريخ</label>
            <input
              type={toFocused || dateTo ? "date" : "text"}
              placeholder="اختر تاريخ الانتهاء"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              onFocus={() => setToFocused(true)}
              onBlur={() => setToFocused(false)}
              className={`custom-date${!dateTo ? " empty" : ""}`}
            />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", paddingTop: 18 }}>
            <button type="submit"
              style={{ flex: 1, padding: "9px 0", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <FaFilter size={11} /> تصفية
            </button>
            <button type="button" onClick={handleClearFilters}
              style={{ padding: "9px 14px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              ↺ ضبط
            </button>
          </div>
        </div>
      </form>

      {/* ── Loading skeleton ────────────────────────────── */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 72, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────── */}
      {!loading && (!transfers || transfers.length === 0) && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 52 }}>💸</div>
          <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد تحويلات أو توريدات تطابق البحث</p>
        </div>
      )}

      {/* ── Transfer cards ───────────────────────────────── */}
      {!loading && transfers && transfers.length > 0 && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {transfers.map((item, idx) => {
              const fromName = getAdminName(item.admin || item.from_admin || item.from);
              const toName   = getAdminName(item.to_admin || item.to);
              const pending  = isPending(item.status);
              return (
                <div key={item.id || idx}
                  style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(239,68,68,.1)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
                >
                  {/* icon */}
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: item.type === "transfer" ? "#dbeafe" : "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                    {item.type === "transfer" ? "⇄" : "⬆"}
                  </div>

                  {/* from → to */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <TypePill type={item.type} />
                      <StatusPill status={item.status} />
                    </div>
                    <div style={{ fontSize: 14, color: "#334155", fontWeight: 600 }}>
                      <span style={{ color: "var(--main-color)" }}>{fromName}</span>
                      <span style={{ color: "#94a3b8", margin: "0 8px" }}>→</span>
                      <span style={{ color: "#0ea5e9" }}>{toName}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                      📅 {formatDate(item.date || item.created_at)}
                    </div>
                  </div>

                  {/* amount */}
                  <div style={{ flexShrink: 0, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "var(--green-color)" }}>{formatCurrency(item.amount)}</div>
                  </div>

                  {/* actions */}
                  <div style={{ flexShrink: 0, display: "flex", gap: 6 }}>
                    {pending ? (
                      <>
                        <button onClick={() => handleApprove(item.id)} disabled={actionLoading}
                          style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: "none", background: "#d1fae5", color: "#065f46", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                          <FaCheck size={11} /> قبول
                        </button>
                        <button onClick={() => handleReject(item.id)} disabled={actionLoading}
                          style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                          <FaTimes size={11} /> رفض
                        </button>
                      </>
                    ) : (
                      <span style={{ color: "#cbd5e1", fontSize: 20, padding: "0 8px" }}>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <PaginationBar meta={transfersMeta} currentPage={currentPage} loading={loading} onChange={(p) => { if (p !== currentPage) setCurrentPage(p); }} />
        </>
      )}

      {/* ════════════════════════════════════════════════════
          Create Transfer Modal
      ════════════════════════════════════════════════════ */}
      {showCreateModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            {/* header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>⇄ تحويل / توريد جديد</span>
              <button onClick={() => setShowCreateModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>

            <form onSubmit={handleCreateTransferSubmit}>
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
                {/* info note */}
                <div style={{ background: "#eff6ff", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#1e40af", display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <FaInfoCircle style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    سيتم تحديد نوع العملية تلقائياً:<br />
                    • <strong>تحويل:</strong> للموظفين (أدمن) &nbsp;• <strong>توريد:</strong> للسوبر أدمن
                  </div>
                </div>

                {/* receiver */}
                <div>
                  <label style={labelS}>الموظف المستلم <span style={{ color: "var(--red-color)" }}>*</span></label>
                  <select value={toAdminId} onChange={(e) => setToAdminId(e.target.value)} required className="custom-select">
                    <option value="">اختر الموظف...</option>
                    {adminWallets?.filter((w) => w.owner_type === "admin" || w.admin_id !== null)?.map((wallet) => {
                      let adminId = wallet?.owner_id ?? wallet?.admin_id;
                      if (adminId && typeof adminId === "object") adminId = adminId.id;
                      const finalId = adminId ?? wallet?.id;
                      return { wallet, finalId };
                    })
                      // BUG-75 fix: استبعاد الموظف المسجل دخوله حالياً من قائمة "الموظف المستلم" لمنع التحويل لنفس الحساب
                      .filter(({ finalId }) => !loggedUserId || String(finalId) !== String(loggedUserId))
                      .map(({ wallet, finalId }) => (
                        <option key={wallet.id} value={finalId}>{wallet.user_name || `موظف #${finalId}`}</option>
                      ))}
                  </select>
                </div>

                {/* amount */}
                <div>
                  <label style={labelS}>المبلغ (ج.م) <span style={{ color: "var(--red-color)" }}>*</span></label>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value.replace(/-/g, ""))} required min="0.01" step="0.01" placeholder="0.00" style={inputS} onFocus={focus} onBlur={blur} />
                </div>

                {/* date */}
                <div>
                  <label style={labelS}>تاريخ العملية <span style={{ color: "var(--red-color)" }}>*</span></label>
                  <input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} required style={inputS} onFocus={focus} onBlur={blur} />
                </div>
              </div>

              {/* footer */}
              <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
                <button type="submit" disabled={actionLoading} style={{ padding: "9px 24px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  {actionLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الإرسال...</> : "إرسال التحويل"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transfers;
